import React, { useEffect, useState } from "react";
import { Check, X, RefreshCw } from "lucide-react";
import PeopleLayout from "../../components/PeopleLayout";
import ConfidenceBadge from "../../components/ConfidenceBadge";
import SelfiePreview from "../../components/SelfiePreview";
import {
  peopleApi,
  hasCapability,
  formatDetail,
} from "../../lib/api";

const CAP_APPROVE = "attendance.review.approve";
const CAP_REJECT = "attendance.review.reject";

export default function PeopleReviews() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [acting, setActing] = useState({});

  const canApprove = hasCapability(CAP_APPROVE);
  const canReject = hasCapability(CAP_REJECT);

  const refresh = async () => {
    try {
      const r = await peopleApi.get("/people/reviews/pending");
      setQueue(r.data);
      setErr("");
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 8000);
    return () => clearInterval(i);
  }, []);

  const act = async (id, verb) => {
    setActing((s) => ({ ...s, [id]: verb }));
    try {
      await peopleApi.post(`/people/reviews/${id}/${verb}`, {
        notes: verb === "reject" ? "Rejected via People's Engine" : "Approved via People's Engine",
      });
      await refresh();
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail) || `Could not ${verb}`);
    } finally {
      setActing((s) => ({ ...s, [id]: null }));
    }
  };

  return (
    <PeopleLayout
      title="Review Queue"
      subtitle="Pending exceptions awaiting decision · capability-gated"
      actions={
        <button
          onClick={refresh}
          data-testid="pe-reviews-refresh"
          className="h-10 px-4 border border-neutral-700 hover:border-amber-400 hover:text-amber-300 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      }
    >
      {!canApprove && !canReject && (
        <div
          data-testid="pe-reviews-no-cap"
          className="border border-amber-500/40 bg-amber-500/10 p-6 mb-6 font-mono text-xs text-amber-200"
        >
          Your role does not include review capabilities. Ask Control Room for{" "}
          <code>attendance.review.approve</code>.
        </div>
      )}
      {loading && <div className="font-mono text-xs text-neutral-500">Loading…</div>}
      {!loading && queue.length === 0 && (
        <div className="border border-neutral-800 bg-[#13161C] p-12 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-neutral-500">
            Queue empty
          </div>
          <p className="mt-2 text-neutral-400">All exceptions have been resolved.</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {queue.map((ev) => (
          <div
            key={ev.id}
            className="border border-neutral-800 bg-[#13161C] p-4"
            data-testid={`pe-review-card-${ev.id}`}
          >
            <div className="flex items-center gap-3">
              {ev.source === "PWA" ? (
                <SelfiePreview scanId={ev.id} size={56} />
              ) : ev.employee_photo_url ? (
                <img
                  src={ev.employee_photo_url}
                  alt=""
                  className="w-14 h-14 object-cover grayscale border border-neutral-700"
                />
              ) : null}
              <div className="flex-1">
                <div className="font-semibold text-neutral-100">
                  {ev.employee_name || "Unknown"}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-neutral-500 mt-0.5">
                  {new Date(ev.created_at_iso).toLocaleString("en-GB")} · {ev.source || "TERMINAL"}
                </div>
              </div>
              <ConfidenceBadge tier={ev.confidence_tier} score={ev.confidence_score} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-[11px] text-neutral-400">
              <div><span className="text-neutral-600">device</span> {ev.device_id || ev.device_fingerprint || "—"}</div>
              <div><span className="text-neutral-600">outlet</span> {ev.outlet_id}</div>
              <div><span className="text-neutral-600">action</span> {ev.attendance_action || "—"}</div>
              <div><span className="text-neutral-600">decision</span> {ev.decision}</div>
              {ev.offline_delay_minutes != null && (
                <div><span className="text-neutral-600">offline</span> {ev.offline_delay_minutes} min</div>
              )}
              {ev.gps_lat != null && (
                <div className="col-span-2">
                  <span className="text-neutral-600">gps</span>{" "}
                  {ev.gps_lat.toFixed(4)}, {ev.gps_lng.toFixed(4)}
                </div>
              )}
            </div>
            {ev.reason_codes?.length > 0 && (
              <div className="mt-2 font-mono text-[11px] text-amber-300/80">
                reason: {ev.reason_codes.join(" · ")}
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <button
                data-testid={`pe-approve-${ev.id}`}
                onClick={() => act(ev.id, "approve")}
                disabled={!canApprove || acting[ev.id]}
                className="h-10 px-4 bg-amber-400 text-black font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                title={!canApprove ? "Missing capability: attendance.review.approve" : ""}
              >
                <Check size={14} /> {acting[ev.id] === "approve" ? "Approving…" : "Approve"}
              </button>
              <button
                data-testid={`pe-reject-${ev.id}`}
                onClick={() => act(ev.id, "reject")}
                disabled={!canReject || acting[ev.id]}
                className="h-10 px-4 border border-red-500 text-red-400 hover:bg-red-500/10 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                title={!canReject ? "Missing capability: attendance.review.reject" : ""}
              >
                <X size={14} /> {acting[ev.id] === "reject" ? "Rejecting…" : "Reject"}
              </button>
            </div>
          </div>
        ))}
      </div>
      {err && (
        <div className="mt-4 text-red-400 font-mono text-sm" data-testid="pe-reviews-error">
          {err}
        </div>
      )}
    </PeopleLayout>
  );
}
